import { useState, useEffect, SetStateAction, Dispatch } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { PADDING } from '../../../constants/parameters';
import { resizeCenterDoc } from '@/utils/utils';
import { Stage } from 'konva/lib/Stage';
import { Image as KonvaImage } from 'konva/lib/shapes/Image';

interface PDFPage {
  getViewport: (opts: { scale: number }) => { width: number; height: number };
}

type CanvasDocumentProps = {
  documentType: string;
  imageData?: { image: string };
  pageIndex: number;
  stageRef: React.RefObject<Stage>;
  konvaImageRef: React.RefObject<KonvaImage>;
  setOffset: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setPosition: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setPositionPDF: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setPageHeight: Dispatch<SetStateAction<number>>;
  setPageWidth: Dispatch<SetStateAction<number>>;
  setDocumentRatio: Dispatch<SetStateAction<number>>;
  setIsDocumentLoaded: Dispatch<SetStateAction<boolean>>;
  IMGDocStatus: string;
  IMGDoc?: HTMLImageElement;
};

export const useCanvasDocument = ({
  documentType,
  imageData,
  pageIndex,
  stageRef,
  konvaImageRef,
  setOffset,
  setPosition,
  setPositionPDF,
  setPageHeight,
  setPageWidth,
  setDocumentRatio,
  setIsDocumentLoaded,
  IMGDocStatus,
  IMGDoc,
}: CanvasDocumentProps) => {
  const [numPages, setNumPages] = useState(1);
  const [firstRenderPDF, setFirstRenderPDF] = useState(false);
  const [lastLoadedPage, setLastLoadedPage] = useState(0);

  /* Display image in the stage */
  useEffect(() => {
    if (documentType !== 'image' || !stageRef.current || !imageData) return;
    const node = konvaImageRef.current;
    if (!node) {
      return; 
    }

    const handleImageLoad = (img: HTMLImageElement) => {
      const wStage = stageRef.current?.width() || 0;
      const hStage = stageRef.current?.height() || 0;

      const {
        wResizedDoc,
        hResizedDoc,
        bestRatio,
        xOffset,
        yOffset,
        xPos,
        yPos,
      } = resizeCenterDoc(img.width, img.height, wStage, hStage, PADDING);

      if (konvaImageRef.current) {
        konvaImageRef.current.image(img);
        konvaImageRef.current.width(wResizedDoc);
        konvaImageRef.current.height(hResizedDoc);
        konvaImageRef.current.offset({
          x: xOffset,
          y: yOffset,
        });
      }
      setPageHeight(hResizedDoc);
      setPageWidth(wResizedDoc);
      setOffset({ x: xOffset, y: yOffset });
      setPosition({ x: xPos, y: yPos });
      setDocumentRatio(bestRatio);
      setIsDocumentLoaded(true);
    };

    if (IMGDocStatus === 'loaded' && IMGDoc) {
      handleImageLoad(IMGDoc);
    } else if (imageData?.image) {
      const img = new Image();
      img.src = `data:image/png;base64,${imageData.image}`;
      img.onload = () => handleImageLoad(img);
    }
  }, [
    documentType,
    IMGDocStatus,
    IMGDoc,
    imageData,
    stageRef,
    konvaImageRef.current,
    setOffset,
    setPosition,
    setDocumentRatio,
    setIsDocumentLoaded,
  ]);

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages);
  };

  const onPageLoadSuccess = (page: PDFPage) => {
    if (firstRenderPDF && pageIndex === lastLoadedPage) {
      return;
    }

    const viewport = page.getViewport({ scale: 1 }); // tient compte de /Rotate
    const wPage: number = viewport.width;
    const hPage: number = viewport.height;
    const wStage: number = stageRef.current?.width() || 0;
    const hStage: number = stageRef.current?.height() || 0;

    const {
      wResizedDoc,
      hResizedDoc,
      bestRatio,
      xOffset,
      yOffset,
      xPos,
      yPos,
    } = resizeCenterDoc(wPage, hPage, wStage, hStage, PADDING);

    const xPosPDF = (wStage - wResizedDoc) / 2;
    const yPosPDF = (hStage - hResizedDoc) / 2;

    setOffset({ x: xOffset, y: yOffset });
    setPosition({ x: xPos, y: yPos });
    setPositionPDF({ x: xPosPDF, y: yPosPDF });
    setPageHeight(hResizedDoc);
    setPageWidth(wResizedDoc);
    setDocumentRatio(bestRatio);
    setIsDocumentLoaded(true);
    setFirstRenderPDF(true);
    setLastLoadedPage(pageIndex);
  };

  return {
    numPages,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
  };
};
